import { CommonModule } from '@angular/common'
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core'
import { MatBadgeModule } from '@angular/material/badge'
import { PAGE_KEY } from '../../constants/page.constants'
import { ConfigService } from '../../services/config.service'
import { EventService } from '../../services/event.service'
import { RouterService, RouterPage } from '../../services/router.service'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { SubtitleData, VODLanguage, VODStatus, VODVideo } from '../../interfaces/vod'
import { VODService } from '../../services/vod.service'
import { ChannelPosition } from '../../pages/channel/channel-position'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { VODTracksMenuComponent } from '../../layout/components/vod-tracks-menu/vod-tracks-menu.component'
import { PlatformService } from '../../platforms/platform.service'
import { LogService } from 'htv-lib'
import { ChannelTrackData, ChannelTrackDataFlags, CurrentChannelData } from '../../interfaces/channel-track-data'
import { VODProgressComponent } from '../../layout/components/vod-progress/vod-progress.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import shaka from 'shaka-player'

@Component({
  selector: 'app-vod-player',
  standalone: true,
  imports: [
    CommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    VODTracksMenuComponent,
    VODProgressComponent,
  ],
  templateUrl: './vod-player.component.html',
  styleUrls: ['./vod-player.component.scss'],
})
export class VODPlayerComponent extends BaseWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  vod?: VODVideo
  vodLang?: VODLanguage

  isVODPage!: boolean

  speed = 1
  isLoading = true
  isPlaying = false
  overlayVisible = false
  trackMenuVisible = false
  useNativeTrackMenu = true
  hideOverlayTimeout: any

  @ViewChild('vodContainer') vodContainer!: ElementRef
  @ViewChild('backButton', { read: ElementRef }) backButton?: ElementRef<HTMLButtonElement>
  @ViewChild('shakaContainer') shakaContainer?: ElementRef<HTMLDivElement>
  @ViewChild('shakaVideoElement') shakaVideoElementRef?: ElementRef<HTMLVideoElement>

  useShakaPlayer = false
  playbackSpeedEnabled = false

  private shakaPlayer?: shaka.Player
  private videoElement?: HTMLVideoElement
  private shakaPlayerLoaded = false
  private shakaPlayerVideoCanPlay = false

  subtitleEnabled = false
  subtitle?: string
  subtitleTimeout: any
  subtitleVisible = false
  subtitleVisibleTimeout: any

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private cdr: ChangeDetectorRef,
    private configService: ConfigService,
    private eventService: EventService,
    private logService: LogService,
    private platformService: PlatformService,
    private routerService: RouterService,
    private vodService: VODService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()

    this.vod = this.vodService.selectedVOD
    this.vodLang = this.vodService.getVODLang(this.vod)

    // Use current page to know if we're on vod page or another (to handle overlay etc)
    this.isVODPage = this.routerService.isCurrentPage(RouterPage.VOD)

    this.useShakaPlayer = !this.platformService.hasCustomVOD()
    this.playbackSpeedEnabled = !this.useShakaPlayer

    this.subscriptions.add(
      this.pageService.getSubject(PAGE_KEY.TRACK_MENU_OVERRIDE)?.subscribe((hasOverride: boolean | undefined) => {
        if (hasOverride !== undefined) {
          this.useNativeTrackMenu = !hasOverride
        } else {
          this.useNativeTrackMenu = true
        }
      })
    )
  }

  ngAfterViewInit(): void {
    if (this.isVODPage) {
      this.registerEventListener()
    }

    if (!this.vod) {
      return
    }

    this.pageService.updateSubject(PAGE_KEY.SELECTED_VOD, this.vod)

    this.vodService.initVOD(this.vod.id)
    this.registerVODListeners()
    this.loadVOD()
    this.showOverlay()
  }

  override ngOnDestroy(): void {
    clearTimeout(this.hideOverlayTimeout)
    clearTimeout(this.subtitleTimeout)
    clearTimeout(this.subtitleVisibleTimeout)

    this.shakaPlayer?.removeEventListener('abrstatuschanged', this.shakaAbrStatusChanged.bind(this))
    this.shakaPlayer?.removeEventListener('adaptation', this.shakaAdaptation.bind(this))
    this.shakaPlayer?.removeEventListener('audiotrackschanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer?.removeEventListener('buffering', this.shakaBuffering.bind(this))
    this.shakaPlayer?.removeEventListener('complete', this.shakaComplete.bind(this))
    this.shakaPlayer?.removeEventListener('configurationchanged', this.shakaConfigurationChanged.bind(this))
    this.shakaPlayer?.removeEventListener('downloadcompleted', this.shakaDownloadCompleted.bind(this))
    this.shakaPlayer?.removeEventListener('downloadfailed', this.shakaDownloadFailed.bind(this))
    this.shakaPlayer?.removeEventListener('drmsessionupdate', this.shakaDrmSessionUpdate.bind(this))
    this.shakaPlayer?.removeEventListener('emsg', this.shakaEmsg.bind(this))
    this.shakaPlayer?.removeEventListener('error', this.shakaError.bind(this))
    this.shakaPlayer?.removeEventListener('expirationupdated', this.shakaExpirationUpdated.bind(this))
    this.shakaPlayer?.removeEventListener('loading', this.shakaLoading.bind(this))
    this.shakaPlayer?.removeEventListener('loaded', this.shakaLoaded.bind(this))
    this.shakaPlayer?.removeEventListener('stalldetected', this.shakaStallDetected.bind(this))
    this.shakaPlayer?.removeEventListener('textchanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer?.removeEventListener('trackschanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer?.removeEventListener('variantchanged', this.shakaVariantChanged.bind(this))

    this.videoElement?.removeEventListener('abort', this.videoAbort.bind(this))
    this.videoElement?.removeEventListener('canplay', this.videoCanPlay.bind(this))
    this.videoElement?.removeEventListener('canplaythrough', this.videoCanPlayThrough.bind(this))
    this.videoElement?.removeEventListener('emptied', this.videoEmptied.bind(this))
    this.videoElement?.removeEventListener('encrypted', this.videoEncrypted.bind(this))
    this.videoElement?.removeEventListener('ended', this.videoEnded.bind(this))
    this.videoElement?.removeEventListener('error', this.videoError.bind(this))
    this.videoElement?.removeEventListener('loadedmetadata', this.videoLoadedMetadata.bind(this))
    this.videoElement?.removeEventListener('pause', this.videoPause.bind(this))
    this.videoElement?.removeEventListener('playing', this.videoPlaying.bind(this))
    this.videoElement?.removeEventListener('progress', this.videoProgress.bind(this))
    this.videoElement?.removeEventListener('seeked', this.videoSeeked.bind(this))
    this.videoElement?.removeEventListener('seeking', this.videoSeeking.bind(this))
    this.videoElement?.removeEventListener('stalled', this.videoStalled.bind(this))
    this.videoElement?.removeEventListener('suspend', this.videoSuspend.bind(this))
    this.videoElement?.removeEventListener('timeupdate', this.videoTimeUpdate.bind(this))
    this.videoElement?.removeEventListener('waiting', this.videoWaiting.bind(this))
    this.videoElement?.removeEventListener('waitingforkey', this.videoWaitingForKey.bind(this))

    this.shakaPlayer?.destroy()
    this.shakaPlayerLoaded = false
    this.shakaPlayerVideoCanPlay = false

    this.vodService.stopVOD()
    this.subscriptions.unsubscribe()

    this.speed = 1
    this.isLoading = true
    this.isPlaying = false

    super.ngOnDestroy()
  }

  registerEventListener(): void {
    this.subscriptions.add(
      this.eventService.keyDown.subscribe((key: string) => {
        switch (key) {
          case 'BACK':
            if (this.trackMenuVisible) {
              this.hideTrackMenu()
              return
            }

            if (this.backButton) {
              if (this.appButtonDirective.selectedButton == this.backButton.nativeElement) {
                this.routerService.goBack()
              } else {
                this.appButtonDirective.selectButton(this.backButton?.nativeElement)
                this.showOverlay()
              }
            } else {
              this.routerService.goBack()
            }
            break
          case 'COL-BLUE':
          case 'SUBTITLE':
            if (this.useNativeTrackMenu) {
              this.toggleTrackMenu()
            } else {
              this.showOverlay()
            }
            break
          default:
            if (this.trackMenuVisible) {
              return
            }
            this.showOverlay()
            break
        }
      })
    )
  }

  registerVODListeners(): void {
    if (!this.vod || !this.vod.state) {
      return
    }

    this.subscriptions.add(
      this.vod.state.status$.subscribe((status: VODStatus) => {
        this.logService.debug(`[VODPlayer] status = ${status}`)
        switch (status) {
          case VODStatus.NONE:
            this.isLoading = true
            this.isPlaying = false
            this.cdr.detectChanges()
            break
          case VODStatus.LOADING:
            this.isLoading = true
            this.isPlaying = false
            this.cdr.detectChanges()
            break
          case VODStatus.IDLE:
            this.isLoading = false
            this.isPlaying = false
            this.cdr.detectChanges()
            break
          case VODStatus.READY:
            this.isLoading = false
            this.refreshTracks()
            this.cdr.detectChanges()
            break
          case VODStatus.PLAYING:
            this.isLoading = false
            this.isPlaying = true
            this.refreshTracks()
            this.cdr.detectChanges()
            break
          case VODStatus.PAUSED:
            this.isLoading = false
            this.isPlaying = false
            this.refreshTracks()
            this.cdr.detectChanges()
            break
          case VODStatus.COMPLETED:
            this.isLoading = false
            this.speed = 1
            this.isPlaying = false
            this.showOverlay()
            this.cdr.detectChanges()
            break
          default:
            break
        }
      })
    )

    this.subscriptions.add(
      this.vod.state.subtitleEnabled$.subscribe((enabled: boolean) => {
        this.subtitleEnabled = enabled
        this.cdr.detectChanges()
      })
    )

    this.subscriptions.add(
      this.vod.state.subtitleChange$.subscribe((subtitle?: SubtitleData) => {
        if (subtitle && subtitle.text) {
          this.showSubtitle(subtitle.text, subtitle.duration)
        }
      })
    )
  }

  async loadVOD(): Promise<void> {
    if (!this.vod) {
      return
    }

    this.logService.debug(`[VODPlayer] Starting VOD ${this.vod.id}`)
    this.logService.debug(`[VODPlayer] VOD url = ${this.vod.videoUrl}`)

    if (this.useShakaPlayer) {
      this.initShakaPlayer()
    } else {
      const boundingRect: DOMRect = this.vodContainer.nativeElement.getBoundingClientRect()
      const pos: ChannelPosition = {
        left: boundingRect.left,
        top: boundingRect.top,
        width: boundingRect.width,
        height: boundingRect.height,
      }
      this.vodService.startVOD(this.vod.id, pos)
    }
  }

  async refreshTracks(): Promise<void> {
    if (this.useShakaPlayer) {
      // do nothing
    } else {
      try {
        const channelData = await this.platformService.getUDPCurrentProgramData()
        if (channelData) {
          this.vod?.state?.tracks$.next(channelData)
        } else {
          // do nothing
        }
      } catch (err: any) {
        this.logService.debug(`[VODPlayer] Could not retrieve VOD tracks, error = ${err?.message}`)
      }
    }
  }

  onAudioTrackChange(track: ChannelTrackData): void {
    if (this.useShakaPlayer) {
      this.shakaPlayer?.selectAudioTrack(track.data)
    } else {
      this.platformService.setUDPAudioTrack(track)
      this.refreshTracks()
    }
  }

  async onSubtitleTrackChange(event: { track: ChannelTrackData; enabled: boolean }): Promise<void> {
    if (this.useShakaPlayer) {
      if (event.enabled) {
        this.shakaPlayer?.selectTextTrack(event.track.data)
      } else {
        this.shakaPlayer?.selectTextTrack(null)
      }
    } else {
      await this.platformService.setSubtitleState(event.enabled)
      this.platformService.setUDPSubtitleTrack(event.track)
      this.refreshTracks()
    }
  }

  toggleTrackMenu(): void {
    if (!this.isVODPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = !this.trackMenuVisible
  }

  showTrackMenu(): void {
    if (!this.isVODPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = true
  }

  hideTrackMenu(): void {
    if (!this.isVODPage || !this.useNativeTrackMenu) {
      return
    }
    this.trackMenuVisible = false
  }

  showOverlay(): void {
    if (!this.isVODPage) {
      return
    }

    // widget overlay
    this.overlayVisible = true
    this.pageService.updateSubject(PAGE_KEY.OVERLAY_VISIBILITY, true)

    // component overlay
    const overlay = document.getElementById('vod-overlay')
    if (overlay) {
      overlay.style.opacity = '1'
    }

    this.registerOverlayTimeout()
  }

  hideOverlay(): void {
    if (!this.isVODPage) {
      return
    }

    // keep overlay when not playing
    if (!this.isPlaying) {
      this.registerOverlayTimeout()
      return
    }

    // keep overlay when on tracks menu
    if (this.trackMenuVisible) {
      this.registerOverlayTimeout()
      return
    }

    // Reset button when hiding overlay
    this.appButtonDirective.forceRefreshSelectedButton()

    // widget overlay
    this.overlayVisible = false
    this.pageService.updateSubject(PAGE_KEY.OVERLAY_VISIBILITY, false)

    // component overlay
    const overlay = document.getElementById('vod-overlay')
    if (overlay) {
      overlay.style.opacity = '0'
    }
  }

  registerOverlayTimeout(): void {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout)
    }
    this.hideOverlayTimeout = setTimeout(() => {
      this.hideOverlay()
    }, this.configService.channelOverlayDurationInMilliseconds)
  }

  showSubtitle(text: string, duration: number) {
    clearTimeout(this.subtitleTimeout)
    clearTimeout(this.subtitleVisibleTimeout)

    if (this.subtitleEnabled) {
      this.subtitle = text
      this.subtitleVisible = true
      this.subtitleTimeout = setTimeout(() => (this.subtitle = undefined), duration)
      this.subtitleVisibleTimeout = setTimeout(() => (this.subtitleVisible = false), duration - 250)
      this.cdr.detectChanges()
    }
  }

  back(): void {
    this.routerService.goBack()
  }

  play(): void {
    const status = this.vod?.state?.status$.getValue()
    this.logService.info(`[VODPlayer] play (status=${status})`)

    if (status === VODStatus.NONE) {
      return
    }

    if (this.speed != 1) {
      this.setSpeed(1)
    }

    if (this.useShakaPlayer) {
      if (this.videoElement) {
        this.videoElement.play()
      }
    } else {
      this.vodService.play()
    }
  }

  pause(): void {
    this.logService.info(`[VODPlayer] pause`)

    if (this.speed != 1) {
      this.setSpeed(1)
    }

    if (this.useShakaPlayer) {
      if (this.videoElement) {
        this.videoElement.pause()
      }
    } else {
      this.vodService.pause()
    }
  }

  jumpBackward(): void {
    this.logService.info(`[VODPlayer] jumpBackward`)

    if (this.useShakaPlayer) {
      if (this.videoElement) {
        this.videoElement.currentTime -= 30
      }
    } else {
      this.vodService.jumpBackward(30000).catch(() => {
        // do nothing
      })
    }
  }

  jumpForward(): void {
    this.logService.info(`[VODPlayer] jumpForward`)

    if (this.useShakaPlayer) {
      if (this.videoElement) {
        this.videoElement.currentTime += 30
      }
    } else {
      this.vodService.jumpForward(30000).catch(() => {
        // do nothing
      })
    }
  }

  async seekTo(milliseconds: number): Promise<void> {
    this.logService.info(`[VODPlayer] seekTo ms=${milliseconds}`)

    if (this.useShakaPlayer) {
      if (this.videoElement) {
        const seconds = milliseconds / 1000
        this.videoElement.currentTime = seconds
      }
    } else {
      try {
        await this.vodService.seekTo(milliseconds)
        this.vod?.state?.currentTime$.next(milliseconds)
      } catch (err: any) {
        this.logService.error(`[VODPlayer] Could not seek, error = ${err?.message}`)
      }
    }
  }

  setSpeed(speed: number): void {
    this.logService.info(`[VODPlayer] setSpeed speed=${speed}`)

    if (!this.playbackSpeedEnabled) {
      this.logService.debug(`[VODPlayer] Playback speed change is disabled`)
      return
    }

    this.speed = speed
    this.vodService.setSpeed(this.speed)
  }

  fastBackward(): void {
    switch (this.speed) {
      case 1:
        this.setSpeed(-2)
        break
      case -2:
        this.setSpeed(-4)
        break
      case -4:
        this.setSpeed(-8)
        break
      case -8:
        this.setSpeed(-16)
        break
      case -16:
        this.setSpeed(1)
        break
      default:
        this.setSpeed(1)
        break
    }
  }

  fastForward(): void {
    switch (this.speed) {
      case 1:
        this.setSpeed(2)
        break
      case 2:
        this.setSpeed(4)
        break
      case 4:
        this.setSpeed(8)
        break
      case 8:
        this.setSpeed(16)
        break
      case 16:
        this.setSpeed(1)
        break
      default:
        this.setSpeed(1)
        break
    }
  }

  async initShakaPlayer(): Promise<void> {
    if (!this.vod) {
      this.logService.error('[VODPlayer] [ShakaPlayer] VOD not available')
      return
    }

    if (!this.useShakaPlayer) {
      this.logService.error('[VODPlayer] [ShakaPlayer] Shaka Player should not be used')
      return
    }

    if (!this.shakaContainer) {
      this.logService.error('[VODPlayer] [ShakaPlayer] shakaContainer is not loaded')
      return
    }

    if (!this.shakaVideoElementRef) {
      this.logService.error('[VODPlayer] [ShakaPlayer] shakaVideoElementRef is not loaded')
      return
    }

    this.videoElement = this.shakaVideoElementRef.nativeElement
    this.vod.state?.status$.next(VODStatus.LOADING)

    shaka.polyfill.installAll()

    if (!shaka.Player.isBrowserSupported()) {
      this.logService.error('[VODPlayer] [ShakaPlayer] Shaka Player not supported in this browser')
      return
    }

    this.shakaPlayer = new shaka.Player()

    // Use UITextDisplayer instead of NativeTextDisplayer
    // https://shaka-player-demo.appspot.com/docs/api/tutorial-text-displayer.html
    this.shakaPlayer.setVideoContainer(this.shakaContainer.nativeElement)

    await this.shakaPlayer.attach(this.videoElement)

    // shaka player events
    this.shakaPlayer.addEventListener('abrstatuschanged', this.shakaAbrStatusChanged.bind(this))
    this.shakaPlayer.addEventListener('adaptation', this.shakaAdaptation.bind(this))
    this.shakaPlayer.addEventListener('audiotrackschanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer.addEventListener('buffering', this.shakaBuffering.bind(this))
    this.shakaPlayer.addEventListener('complete', this.shakaComplete.bind(this))
    this.shakaPlayer.addEventListener('configurationchanged', this.shakaConfigurationChanged.bind(this))
    this.shakaPlayer.addEventListener('downloadcompleted', this.shakaDownloadCompleted.bind(this))
    this.shakaPlayer.addEventListener('downloadfailed', this.shakaDownloadFailed.bind(this))
    this.shakaPlayer.addEventListener('drmsessionupdate', this.shakaDrmSessionUpdate.bind(this))
    this.shakaPlayer.addEventListener('emsg', this.shakaEmsg.bind(this))
    this.shakaPlayer.addEventListener('error', this.shakaError.bind(this))
    this.shakaPlayer.addEventListener('expirationupdated', this.shakaExpirationUpdated.bind(this))
    this.shakaPlayer.addEventListener('loading', this.shakaLoading.bind(this))
    this.shakaPlayer.addEventListener('loaded', this.shakaLoaded.bind(this))
    this.shakaPlayer.addEventListener('stalldetected', this.shakaStallDetected.bind(this))
    this.shakaPlayer.addEventListener('textchanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer.addEventListener('trackschanged', this.shakaTracksChanged.bind(this))
    this.shakaPlayer.addEventListener('variantchanged', this.shakaVariantChanged.bind(this))

    // video events
    this.videoElement.addEventListener('abort', this.videoAbort.bind(this))
    this.videoElement.addEventListener('canplay', this.videoCanPlay.bind(this))
    this.videoElement.addEventListener('canplaythrough', this.videoCanPlayThrough.bind(this))
    this.videoElement.addEventListener('emptied', this.videoEmptied.bind(this))
    this.videoElement.addEventListener('encrypted', this.videoEncrypted.bind(this))
    this.videoElement.addEventListener('ended', this.videoEnded.bind(this))
    this.videoElement.addEventListener('error', this.videoError.bind(this))
    this.videoElement.addEventListener('loadedmetadata', this.videoLoadedMetadata.bind(this))
    this.videoElement.addEventListener('pause', this.videoPause.bind(this))
    this.videoElement.addEventListener('playing', this.videoPlaying.bind(this))
    this.videoElement.addEventListener('progress', this.videoProgress.bind(this))
    this.videoElement.addEventListener('seeked', this.videoSeeked.bind(this))
    this.videoElement.addEventListener('seeking', this.videoSeeking.bind(this))
    this.videoElement.addEventListener('stalled', this.videoStalled.bind(this))
    this.videoElement.addEventListener('suspend', this.videoSuspend.bind(this))
    this.videoElement.addEventListener('timeupdate', this.videoTimeUpdate.bind(this))
    this.videoElement.addEventListener('waiting', this.videoWaiting.bind(this))
    this.videoElement.addEventListener('waitingforkey', this.videoWaitingForKey.bind(this))

    // unmapped video events
    /*
    this.videoElement.addEventListener('click', () => console.log('(video) click'))
    this.videoElement.addEventListener('durationchange', () => console.log('(video) durationchange'))
    this.videoElement.addEventListener('loadeddata', () => console.log('(video) loadeddata'))
    this.videoElement.addEventListener('loadstart', () => console.log('(video) loadstart'))
    this.videoElement.addEventListener('play', () => console.log('(video) play'))
    this.videoElement.addEventListener('ratechange', () => console.log('(video) ratechange'))
    this.videoElement.addEventListener('volumechange', () => console.log('(video) volumechange'))
    */

    this.logService.info(`[VODPlayer] [ShakaPlayer] Starting VOD ${this.vod.id}`)

    // Configure position of subtitles for Shaka player
    this.shakaPlayer.configure({
      textDisplayer: {
        positionArea: shaka.config.PositionArea.BOTTOM_CENTER,
      },
    })

    const shakaPlatformConfig = this.platformService.getShakaPlatformConfig()
    if (shakaPlatformConfig) {
      this.logService.info(`[VODPlayer] [ShakaPlayer] Loading Shaka platform specific config`)
      this.shakaPlayer.configure(shakaPlatformConfig)
    }

    if (this.vod.drm) {
      this.logService.info(`[VODPlayer] [ShakaPlayer] Initializing DRM`)
      if (this.vod.drmConfig) {
        if (this.vod.drmConfig.widevineLicenseUrl) {
          this.logService.info(
            `[VODPlayer] [ShakaPlayer] Using Widevine license url: ${this.vod.drmConfig.widevineLicenseUrl}`
          )
          this.shakaPlayer.configure({
            drm: {
              servers: {
                'com.widevine.alpha': this.vod.drmConfig.widevineLicenseUrl,
              },
            },
          })

          if (this.vod.drmConfig.headers) {
            this.logService.info(
              `[VODPlayer] [ShakaPlayer] Using Widevine headers: ${JSON.stringify(this.vod.drmConfig.headers)}`
            )
            this.shakaPlayer.configure({
              drm: {
                advanced: {
                  'com.widevine.alpha': {
                    headers: this.vod.drmConfig.headers.reduce((acc, header) => ({ ...acc, ...header })),
                  },
                },
              },
            })
          }
        }
      }
    }

    if (this.vod.videoUrl) {
      try {
        await this.shakaPlayer.load(this.vod.videoUrl)
      } catch (err: any) {
        this.logService.error(
          `[VODPlayer] [ShakaPlayer] Could not load shaka player, error = ${err?.message}, data = ${JSON.stringify(err?.data)}`
        )
      }
    } else {
      this.logService.error(`[VODPlayer] [ShakaPlayer] Empty VOD videoUrl`)
    }
  }

  // Shaka player events

  shakaAbrStatusChanged(): void {
    this.logService.debug(`[VODPlayer] (shaka) abrstatuschanged`)
  }

  shakaAdaptation(e: any): void {
    this.logService.debug(`[VODPlayer] (shaka) adaptation`)
    const { oldTrack, newTrack } = e
    if (oldTrack) {
      this.logService.debug(
        `[VODPlayer] (shaka) adaptation oldTrack ${oldTrack.width}x${oldTrack.height} ${oldTrack.bandwidth}bps ${oldTrack.codecs}`
      )
    }
    if (newTrack) {
      this.logService.debug(
        `[VODPlayer] (shaka) adaptation newTrack ${newTrack.width}x${newTrack.height} ${newTrack.bandwidth}bps ${newTrack.codecs}`
      )
    }
  }

  shakaBuffering(): void {
    this.logService.debug(`[VODPlayer] (shaka) buffering`)
  }

  shakaComplete(): void {
    this.logService.debug(`[VODPlayer] (shaka) complete`)
  }

  shakaConfigurationChanged(): void {
    this.logService.debug(`[VODPlayer] (shaka) configurationchanged`)
  }

  shakaDownloadCompleted(): void {
    this.logService.debug(`[VODPlayer] (shaka) downloadcompleted`)
  }

  shakaDownloadFailed(e: any): void {
    this.logService.debug(`[VODPlayer] (shaka) downloadfailed`)

    const error = e.error
    if (error) {
      this.logService.error(
        `[VODPlayer] (shaka) downloadfailed [${error.code}] ${error.message} data: ${JSON.stringify(error.data)}`
      )
    }
  }

  shakaDrmSessionUpdate(): void {
    this.logService.debug(`[VODPlayer] (shaka) drmsessionupdate`)
  }

  shakaEmsg(): void {
    this.logService.debug(`[VODPlayer] (shaka) emsg`)
  }

  shakaError(e: any): void {
    const error = e?.detail
    if (error) {
      if (error.data) {
        this.logService.error(
          `[VODPlayer] (shaka) error [${error.code}] ${error.message} data: ${JSON.stringify(error.data)}`
        )
      } else {
        this.logService.error(`[VODPlayer] (shaka) error [${error.code}] ${error.message}`)
      }
    } else {
      this.logService.error(`[VODPlayer] (shaka) error`)
    }
  }

  shakaExpirationUpdated(): void {
    this.logService.debug(`[VODPlayer] (shaka) expirationupdated`)
  }

  shakaLoading(): void {
    this.logService.debug(`[VODPlayer] (shaka) loading`)
  }

  shakaLoaded(): void {
    this.logService.debug(`[VODPlayer] (shaka) loaded`)

    this.isLoading = true // Still in buffering
    this.shakaPlayerLoaded = true

    if (this.videoElement && this.videoElement?.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      this.isLoading = false
    }

    if (this.shakaPlayerLoaded && this.shakaPlayerVideoCanPlay) {
      this.vod?.state?.status$.next(VODStatus.READY)
    }
  }

  shakaStallDetected(): void {
    this.logService.debug(`[VODPlayer] (shaka) stalldetected`)
  }

  shakaTracksChanged(): void {
    this.logService.debug(`[VODPlayer] (shaka) trackschanged`)

    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    const textTracks = this.shakaPlayer?.getTextTracks()
    if (textTracks) {
      for (let i = 0; i < textTracks?.length; i++) {
        const track = textTracks[i]
        const flags: string[] = []
        if (track.accessibilityPurpose == 'hard of hearing') {
          flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
        }
        const lang = track.language == 'und' ? 'none' : track.language
        const data: ChannelTrackData = {
          index: track.id,
          lang: lang,
          data: track,
          flags: flags,
        }
        channelData.subtitleTracks.push(data)

        if (track.active) {
          channelData.currentSubtitleTrack = data
        }
      }
    }

    const audioTracks = this.shakaPlayer?.getAudioTracks()
    if (audioTracks) {
      for (let i = 0; i < audioTracks?.length; i++) {
        const track = audioTracks[i]
        const flags: string[] = []
        if (track.accessibilityPurpose == 'visually impaired') {
          flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
        }
        const lang = track.language == 'und' ? 'none' : track.language
        const data: ChannelTrackData = {
          index: i,
          lang: lang,
          data: track,
          flags: flags,
        }
        channelData.audioTracks.push(data)

        if (track.active) {
          channelData.currentAudioTrack = data
        }
      }
    }

    channelData.isSubtitleEnabled = channelData.currentSubtitleTrack != undefined

    this.vod?.state?.tracks$.next(channelData)
  }

  shakaVariantChanged(): void {
    this.logService.debug(`[VODPlayer] (shaka) variantchanged`)
  }

  // Video events

  videoAbort(): void {
    this.logService.debug(`[VODPlayer] (video) abort`)
  }

  videoCanPlay(): void {
    this.logService.debug(`[VODPlayer] (video) canplay`)

    this.isLoading = false
    this.shakaPlayerVideoCanPlay = true

    if (this.shakaPlayerLoaded && this.shakaPlayerVideoCanPlay) {
      this.vod?.state?.status$.next(VODStatus.READY)
    }
  }

  videoCanPlayThrough(): void {
    this.logService.error(`[VODPlayer] (video) canplaythrough`)
  }

  videoEmptied(): void {
    this.logService.debug(`[VODPlayer] (video) emptied`)
  }

  videoEncrypted(): void {
    this.logService.debug(`[VODPlayer] (video) encrypted`)
  }

  videoEnded(): void {
    this.logService.debug(`[VODPlayer] (video) ended`)
  }

  videoError(e: any): void {
    if (this.videoElement) {
      this.logService.error(
        `[VODPlayer] (video) error = ${e?.message}, videoError = ${this.videoElement?.error?.code} ${this.videoElement?.error?.message}`
      )
    } else {
      this.logService.error(`[VODPlayer] (video) error = ${e?.message}`)
    }
  }

  videoLoadedMetadata(): void {
    if (this.videoElement) {
      this.vod?.state?.duration$.next(this.videoElement.duration * 1000)
      this.vod?.state?.currentTime$.next(this.videoElement.currentTime * 1000)
    }
  }

  videoPause(): void {
    this.logService.debug(`[VODPlayer] (video) paused`)
    this.vod?.state?.status$.next(VODStatus.PAUSED)
  }

  videoPlaying(): void {
    this.logService.debug(`[VODPlayer] (video) playing`)
    this.vod?.state?.status$.next(VODStatus.PLAYING)
  }

  videoProgress(): void {
    this.logService.debug(`[VODPlayer] (video) progress`)
  }

  videoSeeked(): void {
    this.logService.debug(`[VODPlayer] (video) seeked`)
  }

  videoSeeking(): void {
    this.logService.debug(`[VODPlayer] (video) seeking`)
  }

  videoStalled(): void {
    this.logService.debug(`[VODPlayer] (video) stalled`)
  }

  videoSuspend(): void {
    this.logService.debug(`[VODPlayer] (video) suspend`)
  }

  videoTimeUpdate(): void {
    if (this.videoElement) {
      this.vod?.state?.currentTime$.next(this.videoElement.currentTime * 1000)
    }
  }

  videoWaiting(): void {
    this.logService.debug(`[VODPlayer] (video) waiting`)
    this.isLoading = true
  }

  videoWaitingForKey(): void {
    this.logService.debug(`[VODPlayer] (video) waitingforkey`)
  }
}
